import { api } from '../../../core/network/api';
import { esc, badge } from '../admin-ui';

/* ── Core payment config types ── */

interface DepositMethod {
  id: string; label: string; icon: string; provider: string;
  configured: boolean; enabled: boolean; active: boolean;
  currencies: string[]; currencyOptions: string[];
  billing: 'required' | 'optional'; minBilling: 'required' | 'optional';
}

interface PayoutMethod {
  id: string; label: string; icon: string; provider: string;
  receiver: 'cardNumber' | 'iban';
  configured: boolean; enabled: boolean; active: boolean;
  currencies: string[]; billing: 'required'; pollable: boolean;
}

interface PaymentLimits {
  depositMinCents: number; depositMaxCents: number;
  depositFeeBps: number; depositFeePct: number;
  depositKycThresholdCents: number; requireVerifiedEmail: boolean;
  withdrawalMinCents: number; withdrawalMaxCents: number;
  withdrawalFeeBps: number; withdrawalFeePct: number;
  capToDeposits: boolean;
}

interface PaymentConfig {
  adopted: boolean; currencies: string[]; defaultCurrency: string;
  withdrawalsEnabled: boolean; kycEnabled: boolean;
  deposit: DepositMethod[]; payout: PayoutMethod[];
  limits: PaymentLimits;
}

/* ── Footer icon types ── */

interface FooterMethod {
  slug: string;
  name: string;
  icon: string;
  iconUrl: string;
  enabled: boolean;
  sortOrder: number;
}

const CONFIG_PATH = '/admin/payments/config';
const WITHDRAWAL_UI_KEY = 'feature.withdrawals';

/* ── Helpers ── */

function statusBadge(cfg: PaymentConfig, kind: 'deposit' | 'payout', m: DepositMethod | PayoutMethod): string {
  if (!m.configured) return badge('Not configured', 'red');
  if (m.active) return badge('Active', 'green');
  if (!m.enabled) return badge('Disabled', 'gray');
  let reason = 'Preview mode';
  if (!cfg.adopted) reason = 'Not adopted';
  else if (m.currencies.length === 0) reason = 'No currency';
  else if (kind === 'payout' && !cfg.withdrawalsEnabled) reason = 'Withdrawals off';
  return `${badge('Inactive', 'yellow')}<div class="mt-1 text-[11px] text-muted">${esc(reason)}</div>`;
}

function toggleSwitch(kind: string, id: string, enabled: boolean, configured: boolean): string {
  return `<label class="relative inline-flex items-center ${configured ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}">
    <input type="checkbox" class="sr-only peer" data-pay-toggle="${kind}:${esc(id)}"
      ${enabled ? 'checked' : ''} ${configured ? '' : 'disabled'} />
    <div class="w-9 h-5 bg-neutral-700 peer-checked:bg-gold/80 rounded-full
      after:content-[''] after:absolute after:top-[2px] after:left-[2px]
      after:bg-white after:rounded-full after:h-4 after:w-4
      after:transition-transform peer-checked:after:translate-x-4"></div>
  </label>`;
}

const eur = (cents: number) => (cents / 100).toFixed(2);

function numField(label: string, key: string, value: string): string {
  return `<div>
    <label class="text-[11px] text-muted/70 uppercase tracking-wider">${label}</label>
    <input data-pay-limit="${key}" type="number" min="0" step="0.01" value="${esc(value)}"
      class="w-full min-h-9 mt-1 px-2 bg-surface-2 text-text border border-border rounded-sm text-[13px]
        focus:outline-none focus:border-gold" />
  </div>`;
}

function checkRow(dataAttr: string, label: string, on: boolean): string {
  return `<label class="flex items-center justify-between rounded border border-border/30 bg-surface-2/40 px-3 py-2.5 cursor-pointer">
    <span class="text-[13px] text-text">${label}</span>
    <input type="checkbox" ${dataAttr} ${on ? 'checked' : ''} class="h-4 w-4 accent-gold" />
  </label>`;
}

function footerMethodCard(m: FooterMethod): string {
  return `
    <div class="flex items-center gap-3 p-3 rounded border border-border/30 hover:bg-surface-2/40 transition-colors">
      <img src="${esc(m.iconUrl)}" alt="${esc(m.name)}" class="w-8 h-6 object-contain" onerror="this.style.display='none'" />
      <span class="text-[13px] text-text flex-1">${esc(m.name)}</span>
      <label class="relative inline-flex items-center cursor-pointer">
        <input type="checkbox" class="sr-only peer" data-footer-slug="${esc(m.slug)}"
          ${m.enabled ? 'checked' : ''} />
        <div class="w-9 h-5 bg-neutral-700 peer-checked:bg-gold/80 rounded-full
          after:content-[''] after:absolute after:top-[2px] after:left-[2px]
          after:bg-white after:rounded-full after:h-4 after:w-4
          after:transition-transform peer-checked:after:translate-x-4"></div>
      </label>
    </div>`;
}

function groupFor(slug: string): string {
  const cards = ['visa', 'mastercard', 'amex', 'maestro', 'discover', 'diners-club', 'jcb', 'unionpay', 'v-pay', 'cartes-bancaires', 'dankort', 'bancomat'];
  const wallets = ['paypal', 'apple-pay', 'google-pay', 'amazon-pay', 'shopify-pay', 'cashapp', 'venmo', 'revolut', 'wise'];
  const bnpl = ['klarna', 'clearpay', 'alma', 'cofidis', 'floa', 'oney', 'scalapay', 'zilch', 'twisto'];
  const banks = ['ideal', 'bancontact', 'eps', 'giropay', 'przelewy24', 'blik', 'trustly', 'sofort', 'sepa', 'sepa-direct-debit', 'bacs-direct-debit', 'bank-generic', 'online-banking', 'pay-by-bank', 'multibanco', 'mybank', 'iris', 'interac', 'mbway', 'postfinance', 'truelayer', 'bizum'];
  const ewallets = ['skrill', 'neteller', 'paysafecard', 'payoneer', 'paysera', 'payu', 'alipay', 'wechat-pay'];
  const badges = ['3d-secure', 'pci-dss'];
  if (cards.includes(slug)) return 'Cards';
  if (wallets.includes(slug)) return 'Digital Wallets';
  if (bnpl.includes(slug)) return 'Buy Now Pay Later';
  if (banks.includes(slug)) return 'Bank / Redirect';
  if (ewallets.includes(slug)) return 'E-Wallets';
  if (badges.includes(slug)) return 'Security Badges';
  return 'Other';
}

/* ── Main mount ── */

export async function mountPayments(container: HTMLElement): Promise<() => void> {
  let disposed = false;
  let cfg: PaymentConfig | null = null;
  let footerMethods: FooterMethod[] = [];
  let footerToggleQueue: Promise<unknown> = Promise.resolve();
  let withdrawalUiVisible = false;

  /* ── Core config PATCH helper ── */

  async function patchConfig(body: Record<string, unknown>, okMsg: string, revert: () => void): Promise<void> {
    try {
      const next = await api.patch<PaymentConfig>(CONFIG_PATH, body);
      if (disposed) return;
      if (next && Array.isArray(next.deposit)) { cfg = next; render(); }
      showMsg(okMsg);
    } catch (err: unknown) {
      if (disposed) return;
      revert();
      const msg = err instanceof Error ? err.message : 'Failed to update';
      showMsg(msg, true);
    }
  }

  function showMsg(text: string, error = false): void {
    const el = container.querySelector<HTMLElement>('#pay-msg');
    if (el) {
      el.textContent = text;
      el.className = `text-[12px] ${error ? 'text-red-400' : 'text-muted'}`;
    }
  }

  /* ── Event delegation ── */

  async function onChange(e: Event): Promise<void> {
    const el = e.target;
    if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLSelectElement)) return;

    // Core deposit/payout method toggle
    if (el instanceof HTMLInputElement && el.dataset.payToggle) {
      if (el.disabled) return;
      const [kind, id] = el.dataset.payToggle.split(':');
      const enabled = el.checked;
      el.disabled = true;
      try {
        await patchConfig({ [kind]: { [id]: { enabled } } }, `${id} ${enabled ? 'enabled' : 'disabled'}`, () => { el.checked = !enabled; });
      } finally { if (el.isConnected) el.disabled = false; }
      return;
    }

    // Billing requirement select
    if (el instanceof HTMLSelectElement && el.dataset.payBilling) {
      const id = el.dataset.payBilling;
      const prev = cfg?.deposit.find((m) => m.id === id)?.billing ?? 'required';
      await patchConfig({ deposit: { [id]: { billing: el.value } } }, 'Billing requirement saved', () => { el.value = prev; });
      return;
    }

    // Withdrawals master toggle
    if (el instanceof HTMLInputElement && el.dataset.payWithdrawals !== undefined) {
      const on = el.checked;
      await patchConfig({ withdrawalsEnabled: on }, `Withdrawals ${on ? 'enabled' : 'disabled'}`, () => { el.checked = !on; });
      return;
    }

    // Boolean limit flags (capToDeposits, requireVerifiedEmail)
    if (el instanceof HTMLInputElement && el.dataset.payFlag) {
      const key = el.dataset.payFlag;
      const on = el.checked;
      await patchConfig({ limits: { [key]: on } }, 'Saved', () => { el.checked = !on; });
      return;
    }

    // Withdrawal UI visibility (feature.withdrawals setting)
    if (el instanceof HTMLInputElement && el.dataset.withdrawalUi !== undefined) {
      const on = el.checked;
      try {
        await api.post('/admin/settings', { [WITHDRAWAL_UI_KEY]: String(on) });
        withdrawalUiVisible = on;
        showMsg(`Withdrawal UI ${on ? 'visible' : 'hidden'} for players`);
      } catch {
        el.checked = !on;
        showMsg('Failed to update withdrawal visibility', true);
      }
      return;
    }

    // Footer icon toggle
    if (el instanceof HTMLInputElement && el.dataset.footerSlug) {
      const slug = el.dataset.footerSlug;
      const enabled = el.checked;
      try {
        // Core's toggle reads, edits and rewrites the whole `payment_methods`
        // JSON, so two toggles in flight at once lose one of them (2026-10-14:
        // switching Visa then Mastercard quickly left only Mastercard on).
        // One at a time, in the order clicked.
        const run = footerToggleQueue.then(() => api.post('/admin/payment-methods/toggle', { slug, enabled }));
        footerToggleQueue = run.catch(() => undefined);
        await run;
        const m = footerMethods.find(x => x.slug === slug);
        if (m) m.enabled = enabled;
        showMsg(`${slug} ${enabled ? 'shown' : 'hidden'} in footer`);
      } catch {
        el.checked = !enabled;
        showMsg(`Failed to toggle ${slug}`, true);
      }
    }
  }

  function onSubmit(e: Event): void {
    const form = e.target as HTMLFormElement;
    if (form.id !== 'pay-wd-limits' && form.id !== 'pay-dep-limits') return;
    e.preventDefault();
    const limits: Record<string, number> = {};
    for (const inp of Array.from(form.querySelectorAll<HTMLInputElement>('[data-pay-limit]'))) {
      const key = inp.dataset.payLimit!;
      const v = Number(inp.value);
      if (!Number.isFinite(v) || v < 0) { showMsg(`Invalid value for ${key}`, true); return; }
      limits[key] = key.endsWith('Cents') ? Math.round(v * 100) : v;
    }
    const btn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    btn.disabled = true;
    void patchConfig({ limits }, 'Limits saved', () => {}).finally(() => { if (btn.isConnected) btn.disabled = false; });
  }

  /* ── Render ── */

  function render(): void {
    if (disposed || !cfg) return;
    const c = cfg;
    const l = c.limits;

    const banner = c.adopted ? '' : `
      <div class="rounded border border-yellow-500/40 bg-yellow-500/10 px-4 py-3 text-[12px] text-yellow-200 mb-4">
        Core payments not adopted — no method is active regardless of toggles.
      </div>`;

    const TH = 'border-b border-border/30 px-3 py-2 text-left text-[11px] text-muted/70 uppercase tracking-wider';
    const TD = 'border-b border-border/20 px-3 py-2.5 text-[13px] text-text';

    const depRows = c.deposit.map((m) => `<tr>
      <td class="${TD}"><span class="font-heading text-[13px]">${esc(m.label)}</span></td>
      <td class="${TD}">${m.currencies.length ? esc(m.currencies.join(', ')) : '<span class="text-muted">—</span>'}</td>
      <td class="${TD}">${m.minBilling === 'required' ? 'Required' :
        `<select data-pay-billing="${esc(m.id)}" class="bg-surface-2 text-text border border-border rounded-sm px-2 py-1 text-[12px]">
          <option value="required" ${m.billing === 'required' ? 'selected' : ''}>Required</option>
          <option value="optional" ${m.billing === 'optional' ? 'selected' : ''}>Optional</option>
        </select>`}</td>
      <td class="${TD}">${toggleSwitch('deposit', m.id, m.enabled, m.configured)}</td>
      <td class="${TD}">${statusBadge(c, 'deposit', m)}</td>
    </tr>`).join('');

    const payRows = c.payout.map((m) => `<tr>
      <td class="${TD}"><span class="font-heading text-[13px]">${esc(m.label)}</span></td>
      <td class="${TD}">${m.currencies.length ? esc(m.currencies.join(', ')) : '<span class="text-muted">—</span>'}</td>
      <td class="${TD}">Required</td>
      <td class="${TD}">${toggleSwitch('payout', m.id, m.enabled, m.configured)}</td>
      <td class="${TD}">${statusBadge(c, 'payout', m)}</td>
    </tr>`).join('');

    const head = `<tr><th class="${TH}">Method</th><th class="${TH}">Currencies</th>
      <th class="${TH}">Billing</th><th class="${TH}">Enabled</th><th class="${TH}">Status</th></tr>`;
    const table = (rows: string) => `<div class="overflow-x-auto rounded border border-border/30 bg-surface-2/20">
      <table class="w-full"><thead>${head}</thead><tbody>${rows}</tbody></table></div>`;

    // Footer icons grouped
    const groups: Record<string, FooterMethod[]> = {};
    for (const m of footerMethods) {
      const group = groupFor(m.slug);
      (groups[group] ??= []).push(m);
    }
    const enabledCount = footerMethods.filter(m => m.enabled).length;
    const footerSection = footerMethods.length === 0 ? '' : `
      <div class="frame-pewter p-4 mb-4">
        <div class="flex items-center justify-between mb-3">
          <h3 class="font-heading text-gold text-sm">Footer Payment Icons</h3>
          ${badge(`${enabledCount} active`, enabledCount > 0 ? 'green' : 'gray')}
        </div>
        <p class="text-[12px] text-muted mb-3">Toggle which payment brand icons appear in the site footer. Decorative only — does not affect checkout.</p>
        ${Object.entries(groups).map(([group, items]) => `
          <div class="mb-4">
            <h4 class="text-[12px] text-muted/70 uppercase tracking-wider mb-2">${esc(group)}</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              ${items.map(m => footerMethodCard(m)).join('')}
            </div>
          </div>
        `).join('')}
      </div>`;

    container.innerHTML = `
      <h2 class="font-heading text-gold text-lg mb-4">Payment Configuration</h2>
      ${banner}

      <div class="frame-pewter p-4 mb-4">
        <h3 class="font-heading text-gold text-sm mb-2">Deposit Methods</h3>
        <p class="text-[12px] text-muted mb-3">Site currencies: ${esc(c.currencies.join(', '))} (default ${esc(c.defaultCurrency)})</p>
        ${table(depRows)}
      </div>

      <div class="frame-pewter p-4 mb-4">
        <h3 class="font-heading text-gold text-sm mb-2">Payout Methods</h3>
        ${payRows.length ? table(payRows) : '<p class="text-[12px] text-muted">No payout methods registered.</p>'}
      </div>

      <div class="frame-pewter p-4 mb-4">
        <div class="flex items-center justify-between mb-2">
          <h3 class="font-heading text-gold text-sm">Withdrawal UI</h3>
          <label class="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" class="sr-only peer" data-withdrawal-ui
              ${withdrawalUiVisible ? 'checked' : ''} />
            <div class="w-9 h-5 bg-neutral-700 peer-checked:bg-gold/80 rounded-full
              after:content-[''] after:absolute after:top-[2px] after:left-[2px]
              after:bg-white after:rounded-full after:h-4 after:w-4
              after:transition-transform peer-checked:after:translate-x-4"></div>
          </label>
        </div>
        <p class="text-[12px] text-muted">
          ${withdrawalUiVisible
            ? 'Withdrawal section is visible to players.'
            : 'Withdrawal section is hidden from players.'}
        </p>
      </div>

      <div class="grid gap-4 sm:grid-cols-2 mb-4">
        <div class="frame-pewter p-4">
          <h3 class="font-heading text-gold text-sm mb-2">Withdrawal Processing</h3>
          <div class="space-y-2 mb-3">
            ${checkRow('data-pay-withdrawals', 'Processing enabled', c.withdrawalsEnabled)}
            ${checkRow('data-pay-flag="capToDeposits"', 'Cap to deposited amount', l.capToDeposits)}
          </div>
          <form id="pay-wd-limits" class="space-y-3">
            <div class="grid grid-cols-3 gap-2">
              ${numField('Fee %', 'withdrawalFeePct', String(l.withdrawalFeePct))}
              ${numField('Min (EUR)', 'withdrawalMinCents', eur(l.withdrawalMinCents))}
              ${numField('Max (EUR)', 'withdrawalMaxCents', eur(l.withdrawalMaxCents))}
            </div>
            <div class="flex justify-end">
              <button type="submit" class="btn btn-gold px-4 text-[13px]">Save</button>
            </div>
          </form>
        </div>

        <div class="frame-pewter p-4">
          <h3 class="font-heading text-gold text-sm mb-2">Deposit Limits</h3>
          <div class="space-y-2 mb-3">
            ${checkRow('data-pay-flag="requireVerifiedEmail"', 'Require verified email', l.requireVerifiedEmail)}
          </div>
          <form id="pay-dep-limits" class="space-y-3">
            <div class="grid grid-cols-2 gap-2">
              ${numField('Fee %', 'depositFeePct', String(l.depositFeePct))}
              ${numField('Min (EUR)', 'depositMinCents', eur(l.depositMinCents))}
              ${numField('Max (EUR)', 'depositMaxCents', eur(l.depositMaxCents))}
              ${numField('KYC threshold (EUR)', 'depositKycThresholdCents', eur(l.depositKycThresholdCents))}
            </div>
            <div class="flex justify-end">
              <button type="submit" class="btn btn-gold px-4 text-[13px]">Save</button>
            </div>
          </form>
        </div>
      </div>

      ${footerSection}

      <div id="pay-msg" class="text-[12px] text-muted mt-2"></div>
    `;
  }

  /* ── Init ── */

  container.addEventListener('change', onChange);
  container.addEventListener('submit', onSubmit);

  try {
    const [config, methods, settings] = await Promise.all([
      api.get<PaymentConfig>(CONFIG_PATH),
      api.get<FooterMethod[]>('/admin/payment-methods').catch(() => [] as FooterMethod[]),
      api.get<Record<string, unknown>>('/admin/settings').catch((): Record<string, unknown> => ({})),
    ]);
    cfg = config;
    footerMethods = methods;
    const wdVal = settings[WITHDRAWAL_UI_KEY];
    withdrawalUiVisible = wdVal === 'true' || wdVal === true;
    render();
  } catch {
    container.innerHTML = '<p class="text-red-400 p-4">Failed to load payment config.</p>';
  }

  return () => {
    disposed = true;
    container.removeEventListener('change', onChange);
    container.removeEventListener('submit', onSubmit);
  };
}
